import fetcher, {
	FleetFile,
	FleetGetListParams,
	FleetPaginated,
	FleetQueryParams,
} from '../fetcher';
import {
	AddressMasterData,
	IndustryMasterData,
	MasterData,
} from './master-data';
import { SurveyInformation } from './survey';
import {
	ApprovalHistory,
	ApprovalRejectVisitReport,
	CustomerBusinessDocument,
	CustomerLegalDocument,
	CustomerReferralDocument,
	FileData,
	MainInformation,
	MarketingAspect,
	OfficeObservationAttendee,
	OfficeObservationCollateral,
	OfficeObservationCollateralAspect,
	OfficeObservationCompetitor,
	OfficeObservationInventory,
	OfficeObservationParetoReceivable,
	OfficeObservationParetoSupplier,
	OfficeObservationPaymentSystem,
	OfficeObservationPhoto,
	OfficeObservationSalesMethod,
	OfficeObservationSparePart,
	OfficeObservationUnit,
	OfficeObservationWarehouse,
	RejectVisitReport,
	SiteReport,
	SiteReportBusinessActivity,
	SiteReportPhoto,
	SiteReportUnitAnalysis,
	TechnicalAspect,
	VisitReportList,
} from './types';

export const getFRMVisitReportList = (params?: FleetQueryParams) => {
	const defaultParams = params
		? params
		: { pagination: { pageSize: 20, pageNumber: 1 } };
	return fetcher<FleetPaginated<VisitReportList>>({
		api: '/visit-reports/frm/get-visit-report-list',
		body: defaultParams,
	});
};

export type VisitReportDetail = {
	approvalHistories: ApprovalHistory[];
	mainInformation: MainInformation;
	customerLegalDocuments: CustomerLegalDocument[];
	customerBusinessDocuments: CustomerBusinessDocument[];
	customerReferralDocuments: CustomerLegalDocument[];
	customerAddress: {
		address: string;
		postalDetail: AddressMasterData;
		rt: string;
		rw: string;
	};
	surveyInformation: SurveyInformation;
	officeObservation: {
		technicalAspect: TechnicalAspect;
		warehouse: OfficeObservationWarehouse;
		collateral: {
			collateralNotes: string;
			collateralVerificationNotes: string;
			collaterals: OfficeObservationCollateral[];
		};
		marketingAspect: MarketingAspect & {
			surveyOfficeObservationId: null;
			salesMethods: OfficeObservationSalesMethod[];
			paymentSystem: OfficeObservationPaymentSystem[];
			competitors: OfficeObservationCompetitor[];
		};
	};
	approval?: {
		approvalHistories: ApprovalHistory[];
	};
};

export type VisitReportRejectDetail = {
	mainInformation: MainInformation;
	customerLegalDocuments: CustomerLegalDocument[];
	customerBusinessDocuments: CustomerBusinessDocument[];
	customerReferralDocuments: CustomerLegalDocument[];
	customerAddress: {
		address: string;
		postalDetail: AddressMasterData;
		rt: string;
		rw: string;
	};
	surveyInformation: SurveyInformation;
	approval?: {
		approvalHistories: ApprovalHistory[];
	};
	rejectInformation: {
		photos: FileData;
		rejectCategory: MasterData;
		rejectNotes: string;
	};
};

export const getFRMVisitReportDetail = (prospectId: string) => {
	return fetcher<VisitReportDetail>({
		api: '/visit-reports/frm/office-observations/get-detail',
		body: {
			prospectId,
		},
	});
};

export const getFBDHRejectVisitReportDetail = (prospectId: string) => {
	return fetcher<VisitReportRejectDetail>({
		api: '/visit-reports/fbdh/get-reject-visit-report-detail',
		body: {
			prospectId,
		},
	});
};

export const submitFRMRejectVisitReport = (body: RejectVisitReport) => {
	return fetcher<string>({
		api: '/visit-reports/frm/request-reject-visit-report',
		body,
	});
};

export const submitFBDHApprovalRejection = (
	body: ApprovalRejectVisitReport,
) => {
	return fetcher<string>({
		api: '/visit-reports/fbdh/submit-approval-rejection',
		body,
	});
};

export const addFRMOfficeObservationAttendee = (
	body: OfficeObservationAttendee<'payload'>,
) => {
	return fetcher<number>({
		api: '/visit-reports/frm/add-office-observation-attendee',
		body,
	});
};

export const addCCAOfficeObservationAttendee = (
	body: OfficeObservationAttendee<'payload'>,
) => {
	return fetcher<number>({
		api: '/visit-reports/cca/add-office-observation-attendee',
		body,
	});
};

export const updateFRMOfficeObservationAttendee = (
	body: OfficeObservationAttendee<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/frm/update-office-observation-attendee',
		body,
	});
};

export const updateCCAOfficeObservationAttendee = (
	body: OfficeObservationAttendee<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/cca/update-office-observation-attendee',
		body,
	});
};

export const deleteFRMOfficeObservationAttendee = (id: number) => {
	return fetcher({
		api: '/visit-reports/frm/delete-office-observation-attendee',
		body: {
			id,
		},
	});
};

export const deleteCCAOfficeObservationAttendee = (id: number) => {
	return fetcher({
		api: '/visit-reports/cca/delete-office-observation-attendee',
		body: {
			id,
		},
	});
};

export const submitFRMOfficeObservationTechnicalAspect = (
	body: TechnicalAspect<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/frm/submit-office-observation-technical-aspect',
		body,
	});
};

export const submitCCAOfficeObservationTechnicalAspect = (
	body: TechnicalAspect<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/frm/submit-office-observation-technical-aspect',
		body,
	});
};

export const addFRMOfficeObservationUnit = (
	body: OfficeObservationUnit<'payload'>,
) => {
	return fetcher<number>({
		api: '/visit-reports/frm/add-office-observation-unit',
		body,
	});
};

export const addCCAOfficeObservationUnit = (
	body: OfficeObservationUnit<'payload'>,
) => {
	return fetcher<number>({
		api: '/visit-reports/cca/add-office-observation-unit',
		body,
	});
};

export const updateFRMOfficeObservationUnit = (
	body: OfficeObservationUnit<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/frm/update-office-observation-unit',
		body,
	});
};

export const updateCCAOfficeObservationUnit = (
	body: OfficeObservationUnit<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/cca/update-office-observation-unit',
		body,
	});
};

export const deleteFRMOfficeObservationUnit = (id: number) => {
	return fetcher({
		api: '/visit-reports/frm/delete-office-observation-unit',
		body: {
			id,
		},
	});
};

export const deleteCCAOfficeObservationUnit = (id: number) => {
	return fetcher({
		api: '/visit-reports/cca/delete-office-observation-unit',
		body: {
			id,
		},
	});
};

export const addFRMOfficeObservationPhoto = (
	body: OfficeObservationPhoto<'payload'>,
) => {
	return fetcher<number>({
		api: '/visit-reports/frm/add-office-observation-photo',
		body,
	});
};

export const addCCAOfficeObservationPhoto = (
	body: OfficeObservationPhoto<'payload'>,
) => {
	return fetcher<number>({
		api: '/visit-reports/cca/add-office-observation-photo',
		body,
	});
};

export const updateFRMOfficeObservationPhoto = (
	body: OfficeObservationPhoto<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/frm/update-office-observation-photo',
		body,
	});
};

export const updateCCAOfficeObservationPhoto = (
	body: OfficeObservationPhoto<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/cca/update-office-observation-photo',
		body,
	});
};

export const deleteFRMOfficeObservationPhoto = (id: number) => {
	return fetcher({
		api: '/visit-reports/frm/delete-office-observation-photo',
		body: {
			id,
		},
	});
};

export const deleteCCAOfficeObservationPhoto = (id: number) => {
	return fetcher({
		api: '/visit-reports/cca/delete-office-observation-photo',
		body: {
			id,
		},
	});
};

export const addFRMOfficeObservationSalesMethod = (
	body: OfficeObservationSalesMethod<'payload'>,
) => {
	return fetcher<OfficeObservationSalesMethod>({
		api: '/visit-reports/frm/add-office-observation-sales-method',
		body,
	});
};

export const addCCAOfficeObservationSalesMethod = (
	body: OfficeObservationSalesMethod<'payload'>,
) => {
	return fetcher<OfficeObservationSalesMethod>({
		api: '/visit-reports/cca/add-office-observation-sales-method',
		body,
	});
};

export const updateFRMOfficeObservationSalesMethod = (
	body: OfficeObservationSalesMethod<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/frm/update-office-observation-sales-method',
		body,
	});
};

export const updateCCAOfficeObservationSalesMethod = (
	body: OfficeObservationSalesMethod<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/cca/update-office-observation-sales-method',
		body,
	});
};

export const deleteFRMOfficeObservationSalesMethod = (
	surveyOfficeObservationSalesMethodId: number,
) => {
	return fetcher({
		api: '/visit-reports/frm/delete-office-observation-sales-method',
		body: {
			surveyOfficeObservationSalesMethodId,
		},
	});
};

export const deleteCCAOfficeObservationSalesMethod = (
	surveyOfficeObservationSalesMethodId: number,
) => {
	return fetcher({
		api: '/visit-reports/cca/delete-office-observation-sales-method',
		body: {
			surveyOfficeObservationSalesMethodId,
		},
	});
};

export const addFRMOfficeObservationPaymentSystem = (
	body: OfficeObservationPaymentSystem<'payload'>,
) => {
	return fetcher<OfficeObservationPaymentSystem>({
		api: '/visit-reports/frm/add-office-observation-payment-system',
		body,
	});
};

export const addCCAOfficeObservationPaymentSystem = (
	body: OfficeObservationPaymentSystem<'payload'>,
) => {
	return fetcher<OfficeObservationPaymentSystem>({
		api: '/visit-reports/cca/add-office-observation-payment-system',
		body,
	});
};

export const updateFRMOfficeObservationPaymentSystem = (
	body: OfficeObservationPaymentSystem<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/frm/update-office-observation-payment-system',
		body,
	});
};

export const updateCCAOfficeObservationPaymentSystem = (
	body: OfficeObservationPaymentSystem<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/cca/update-office-observation-payment-system',
		body,
	});
};

export const deleteFRMOfficeObservationPaymentSystem = (
	surveyOfficeObservationPaymentSystemId: number,
) => {
	return fetcher({
		api: '/visit-reports/frm/delete-office-observation-payment-system',
		body: {
			surveyOfficeObservationPaymentSystemId,
		},
	});
};

export const deleteCCAOfficeObservationPaymentSystem = (
	surveyOfficeObservationPaymentSystemId: number,
) => {
	return fetcher({
		api: '/visit-reports/cca/delete-office-observation-payment-system',
		body: {
			surveyOfficeObservationPaymentSystemId,
		},
	});
};

export const submitFRMOfficeObservationMarketingAspect = (
	body: MarketingAspect,
) => {
	return fetcher({
		api: '/visit-reports/frm/submit-office-observation-marketing-aspect',
		body,
	});
};

export const submitCCAOfficeObservationMarketingAspect = (
	body: MarketingAspect,
) => {
	return fetcher({
		api: '/visit-reports/cca/submit-office-observation-marketing-aspect',
		body,
	});
};

export const addFRMOfficeObservationCompetitor = (
	body: OfficeObservationCompetitor,
) => {
	return fetcher<OfficeObservationCompetitor>({
		api: '/visit-reports/frm/add-office-observation-competitor',
		body,
	});
};

export const addCCAOfficeObservationCompetitor = (
	body: OfficeObservationCompetitor,
) => {
	return fetcher<OfficeObservationCompetitor>({
		api: '/visit-reports/cca/add-office-observation-competitor',
		body,
	});
};

export const updateFRMOfficeObservationCompetitor = (
	body: OfficeObservationCompetitor,
) => {
	return fetcher({
		api: '/visit-reports/frm/update-office-observation-competitor',
		body,
	});
};

export const updateCCAOfficeObservationCompetitor = (
	body: OfficeObservationCompetitor,
) => {
	return fetcher({
		api: '/visit-reports/cca/update-office-observation-competitor',
		body,
	});
};

export const deleteFRMOfficeObservationCompetitor = (
	surveyOfficeObservationCompetitorId: number,
) => {
	return fetcher({
		api: '/visit-reports/frm/delete-office-observation-competitor',
		body: {
			surveyOfficeObservationCompetitorId,
		},
	});
};

export const deleteCCAOfficeObservationCompetitor = (
	surveyOfficeObservationCompetitorId: number,
) => {
	return fetcher({
		api: '/visit-reports/cca/delete-office-observation-competitor',
		body: {
			surveyOfficeObservationCompetitorId,
		},
	});
};

export const addFRMOfficeObservationCollateral = (
	body: OfficeObservationCollateral<'payload'>,
) => {
	return fetcher<number>({
		api: '/visit-reports/frm/add-office-observation-collateral',
		body,
	});
};

export const addCCAOfficeObservationCollateral = (
	body: OfficeObservationCollateral<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/cca/add-office-observation-collateral',
		body,
	});
};

export const updateFRMOfficeObservationCollateral = (
	body: OfficeObservationCollateral<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/frm/update-office-observation-collateral',
		body,
	});
};

export const updateCCAOfficeObservationCollateral = (
	body: OfficeObservationCollateral<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/cca/update-office-observation-collateral',
		body,
	});
};

export const deleteFRMOfficeObservationCollateral = (id: number) => {
	return fetcher({
		api: '/visit-reports/frm/delete-office-observation-collateral',
		body: {
			id,
		},
	});
};

export const deleteCCAOfficeObservationCollateral = (id: number) => {
	return fetcher({
		api: '/visit-reports/cca/delete-office-observation-collateral',
		body: {
			id,
		},
	});
};

export const submitFRMOfficeObservationWarehouse = (
	body: OfficeObservationWarehouse<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/frm/submit-office-observation-warehouse',
		body,
	});
};

export const submitCCAOfficeObservationWarehouse = (
	body: OfficeObservationWarehouse<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/cca/submit-office-observation-warehouse',
		body,
	});
};

export const addFRMOfficeObservationSparePart = (
	body: OfficeObservationSparePart<'payload'>,
) => {
	return fetcher<OfficeObservationSparePart>({
		api: '/visit-reports/frm/add-office-observation-spare-part',
		body,
	});
};

export const addCCAOfficeObservationSparePart = (
	body: OfficeObservationSparePart<'payload'>,
) => {
	return fetcher<OfficeObservationSparePart>({
		api: '/visit-reports/cca/add-office-observation-spare-part',
		body,
	});
};

export const updateFRMOfficeObservationSparePart = (
	body: OfficeObservationSparePart<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/frm/update-office-observation-spare-part',
		body,
	});
};

export const updateCCAOfficeObservationSparePart = (
	body: OfficeObservationSparePart<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/cca/update-office-observation-spare-part',
		body,
	});
};

export const deleteFRMOfficeObservationSparePart = (id: number) => {
	return fetcher({
		api: '/visit-reports/frm/delete-office-observation-spare-part',
		body: {
			surveyOfficeObservationSparePartId: id,
		},
	});
};

export const deleteCCAOfficeObservationSparePart = (id: number) => {
	return fetcher({
		api: '/visit-reports/cca/delete-office-observation-spare-part',
		body: {
			surveyOfficeObservationSparePartId: id,
		},
	});
};

export const addFRMOfficeObservationInventory = (
	body: OfficeObservationInventory<'payload'>,
) => {
	return fetcher<OfficeObservationInventory>({
		api: '/visit-reports/frm/add-office-observation-inventory',
		body,
	});
};

export const addCCAOfficeObservationInventory = (
	body: OfficeObservationInventory<'payload'>,
) => {
	return fetcher<OfficeObservationInventory>({
		api: '/visit-reports/cca/add-office-observation-inventory',
		body,
	});
};

export const updateFRMOfficeObservationInventory = (
	body: OfficeObservationInventory<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/frm/update-office-observation-inventory',
		body,
	});
};

export const updateCCAOfficeObservationInventory = (
	body: OfficeObservationInventory<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/cca/update-office-observation-inventory',
		body,
	});
};

export const deleteFRMOfficeObservationInventory = (id: number) => {
	return fetcher({
		api: '/visit-reports/frm/delete-office-observation-inventory',
		body: {
			surveyOfficeObservationInventoryId: id,
		},
	});
};

export const deleteCCAOfficeObservationInventory = (id: number) => {
	return fetcher({
		api: '/visit-reports/cca/delete-office-observation-inventory',
		body: {
			surveyOfficeObservationInventoryId: id,
		},
	});
};

export const addFRMOfficeObservationParetoSupplier = (
	body: OfficeObservationParetoSupplier<'payload'>,
) => {
	return fetcher<OfficeObservationParetoSupplier>({
		api: '/visit-reports/frm/add-office-observation-pareto-supplier',
		body,
	});
};

export const addCCAOfficeObservationParetoSupplier = (
	body: OfficeObservationParetoSupplier<'payload'>,
) => {
	return fetcher<OfficeObservationParetoSupplier>({
		api: '/visit-reports/cca/add-office-observation-pareto-supplier',
		body,
	});
};

export const updateFRMOfficeObservationParetoSupplier = (
	body: OfficeObservationParetoSupplier<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/frm/update-office-observation-pareto-supplier',
		body,
	});
};

export const updateCCAOfficeObservationParetoSupplier = (
	body: OfficeObservationParetoSupplier<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/cca/update-office-observation-pareto-supplier',
		body,
	});
};

export const deleteFRMOfficeObservationParetoSupplier = (id: number) => {
	return fetcher({
		api: '/visit-reports/frm/delete-office-observation-pareto-supplier',
		body: {
			surveyOfficeObservationParetoSupplierId: id,
		},
	});
};

export const deleteCCAOfficeObservationParetoSupplier = (id: number) => {
	return fetcher({
		api: '/visit-reports/cca/delete-office-observation-pareto-supplier',
		body: {
			surveyOfficeObservationParetoSupplierId: id,
		},
	});
};

export const addFRMOfficeObservationParetoReceivable = (
	body: OfficeObservationParetoReceivable<'payload'>,
) => {
	return fetcher<OfficeObservationParetoReceivable>({
		api: '/visit-reports/frm/add-office-observation-pareto-receivable',
		body,
	});
};

export const addCCAOfficeObservationParetoReceivable = (
	body: OfficeObservationParetoReceivable<'payload'>,
) => {
	return fetcher<OfficeObservationParetoReceivable>({
		api: '/visit-reports/cca/add-office-observation-pareto-receivable',
		body,
	});
};

export const updateFRMOfficeObservationParetoReceivable = (
	body: OfficeObservationParetoReceivable<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/frm/update-office-observation-pareto-receivable',
		body,
	});
};

export const updateCCAOfficeObservationParetoReceivable = (
	body: OfficeObservationParetoReceivable<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/cca/update-office-observation-pareto-receivable',
		body,
	});
};

export const deleteFRMOfficeObservationParetoReceivable = (id: number) => {
	return fetcher({
		api: '/visit-reports/frm/delete-office-observation-pareto-receivable',
		body: {
			surveyOfficeObservationParetoReceivableId: id,
		},
	});
};

export const deleteCCAOfficeObservationParetoReceivable = (id: number) => {
	return fetcher({
		api: '/visit-reports/cca/delete-office-observation-pareto-receivable',
		body: {
			surveyOfficeObservationParetoReceivableId: id,
		},
	});
};

export const submitFRMOfficeObservationCollateralAspect = (
	body: OfficeObservationCollateralAspect,
) => {
	return fetcher({
		api: '/visit-reports/frm/submit-office-observation-collateral-aspect',
		body,
	});
};

export const submitCCAOfficeObservationCollateralAspect = (
	body: OfficeObservationCollateralAspect,
) => {
	return fetcher({
		api: '/visit-reports/cca/submit-office-observation-collateral-aspect',
		body,
	});
};

export const getFRMSiteReportDetail = (prospectId: string) => {
	return fetcher<{
		mainInformation: MainInformation;
		customerLegalDocuments: CustomerLegalDocument[];
		customerBusinessDocuments: CustomerBusinessDocument[];
		customerReferralDocuments: CustomerLegalDocument[];
		surveyInformation: SurveyInformation;
		siteReport: SiteReport;
	}>({
		api: '/visit-reports/frm/site-report/get-detail',
		body: {
			prospectId,
		},
	});
};

export const submitFRMSiteReportDetail = (body: SiteReport<'payload'>) => {
	return fetcher({
		api: '/visit-reports/frm/site-report/submit-visit-detail',
		body,
	});
};

export const submitCCASiteReportDetail = (body: SiteReport<'payload'>) => {
	return fetcher({
		api: '/visit-reports/cca/site-report/submit-visit-detail',
		body,
	});
};

export const submitFRMSiteReportUnitAnalysis = (
	body: SiteReportUnitAnalysis<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/frm/site-report/submit-unit-analysis',
		body,
	});
};

export const submitCCASiteReportUnitAnalysis = (
	body: SiteReportUnitAnalysis<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/cca/site-report/submit-unit-analysis',
		body,
	});
};

export const submitFRMSiteReportBusinessActivity = (
	body: SiteReportBusinessActivity<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/frm/site-report/submit-business-activity',
		body,
	});
};

export const submitCCASiteReportBusinessActivity = (
	body: SiteReportBusinessActivity<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/cca/site-report/submit-business-activity',
		body,
	});
};

export const addFRMSiteReportPhoto = (body: SiteReportPhoto<'payload'>) => {
	return fetcher<{ id: number }>({
		api: '/visit-reports/frm/site-report/add-photo',
		body,
	});
};

export const addCCASiteReportPhoto = (body: SiteReportPhoto<'payload'>) => {
	return fetcher<{ id: number }>({
		api: '/visit-reports/cca/site-report/add-photo',
		body,
	});
};

export const updateFRMSiteReportPhoto = (body: SiteReportPhoto<'payload'>) => {
	return fetcher({
		api: '/visit-reports/frm/site-report/update-photo',
		body,
	});
};

export const updateCCASiteReportPhoto = (body: SiteReportPhoto<'payload'>) => {
	return fetcher({
		api: '/visit-reports/cca/site-report/update-photo',
		body,
	});
};

export const deleteFRMSiteReportPhoto = (id: number) => {
	return fetcher({
		api: '/visit-reports/frm/site-report/delete-photo',
		body: {
			siteReportPhotoId: id,
		},
	});
};

export const deleteCCASiteReportPhoto = (id: number) => {
	return fetcher({
		api: '/visit-reports/cca/site-report/delete-photo',
		body: {
			siteReportPhotoId: id,
		},
	});
};

export const addFRMSiteReportAttendee = (
	body: OfficeObservationAttendee<'payload'>,
) => {
	return fetcher<OfficeObservationAttendee>({
		api: '/visit-reports/frm/site-report/add-attendee',
		body,
	});
};

export const addCCASiteReportAttendee = (
	body: OfficeObservationAttendee<'payload'>,
) => {
	return fetcher<OfficeObservationAttendee>({
		api: '/visit-reports/cca/site-report/add-attendee',
		body,
	});
};

export const updateFRMSiteReportAttendee = (
	body: OfficeObservationAttendee<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/frm/site-report/update-attendee',
		body,
	});
};

export const updateCCASiteReportAttendee = (
	body: OfficeObservationAttendee<'payload'>,
) => {
	return fetcher({
		api: '/visit-reports/cca/site-report/update-attendee',
		body,
	});
};

export const deleteFRMSiteReportAttendee = (id: number) => {
	return fetcher({
		api: '/visit-reports/frm/site-report/delete-attendee',
		body: {
			surveySiteReportAttendeeId: id,
		},
	});
};

export const deleteCCASiteReportAttendee = (id: number) => {
	return fetcher({
		api: '/visit-reports/cca/site-report/delete-attendee',
		body: {
			surveySiteReportAttendeeId: id,
		},
	});
};

export const requestFRMSiteReportVerification = (body: {
	surveyId: number;
	notes: string;
}) => {
	return fetcher({
		api: '/visit-reports/frm/request-verification',
		body,
	});
};

export const getCCAVisitReportList = (params?: FleetQueryParams) => {
	const defaultParams = params
		? params
		: { pagination: { pageSize: 20, pageNumber: 1 } };
	return fetcher<FleetPaginated<VisitReportList>>({
		api: '/visit-reports/cca/get-visit-report-list',
		body: defaultParams,
	});
};

export const getCCAVisitReportDetail = (prospectId: string) => {
	return fetcher<{
		mainInformation: MainInformation;
		customerLegalDocuments: CustomerLegalDocument[];
		customerBusinessDocuments: CustomerBusinessDocument[];
		customerReferralDocuments: CustomerLegalDocument[];
		customerAddress: {
			address: string;
			postalDetail: AddressMasterData;
			rt: string;
			rw: string;
		};
		surveyInformation: SurveyInformation;
		siteReport: SiteReport;
		officeObservation: {
			technicalAspect: TechnicalAspect;
			warehouse: OfficeObservationWarehouse;
			collateral: {
				collateralNotes: string;
				collateralVerificationNotes: string;
				collaterals: OfficeObservationCollateral[];
			};
			marketingAspect: MarketingAspect & {
				surveyOfficeObservationId: null;
				salesMethods: OfficeObservationSalesMethod[];
				paymentSystem: OfficeObservationPaymentSystem[];
				competitors: OfficeObservationCompetitor[];
				bouwheers: Array<{
					id: number;
					prospectId: string;
					bouwheer: {
						id: string;
						name: string;
						type: string;
						industrySector: {
							id: number;
							value: string;
						};
					};
					jobTitle: string;
					spkNumber: string;
					jobLocation: string;
					contractDetail: string;
					contractValue: number;
					termOfPayment?: string;
					experience?: string;
					tenor?: string;
					contractDocument: {
						id: string;
						name: string;
						path: string;
						url: string;
					};
					startDate?: string;
					endDate?: string;
					industrySector: {
						id: number;
						value: string;
					};
					paymentMethod: {
						id: number;
						value: string;
					};
				}>;
			};
		};
		approval: {
			approvalHistories: ApprovalHistory[];
		};
	}>({
		api: '/visit-reports/cca/get-visit-report-detail',
		body: {
			prospectId,
		},
	});
};

export const getFBDHVisitReportList = (params?: FleetQueryParams) => {
	const defaultParams = params
		? params
		: { pagination: { pageSize: 20, pageNumber: 1 } };
	return fetcher<FleetPaginated<VisitReportList>>({
		api: '/visit-reports/fbdh/get-visit-report-list',
		body: defaultParams,
	});
};
export const getFBDHVisitReportRejectionList = () => {
	return fetcher<FleetPaginated<VisitReportList>>({
		api: '/visit-reports/fbdh/get-visit-report-rejection-list',
		body: {},
	});
};

export const getCCDHVisitReportList = (params?: FleetQueryParams) => {
	const defaultParams = params
		? params
		: { pagination: { pageSize: 20, pageNumber: 1 } };
	return fetcher<FleetPaginated<VisitReportList>>({
		api: '/visit-reports/ccdh/get-visit-report-list',
		body: defaultParams,
	});
};

export const getFHVisitReportList = (params?: FleetQueryParams) => {
	const defaultParams = params
		? params
		: { pagination: { pageSize: 20, pageNumber: 1 } };
	return fetcher<FleetPaginated<VisitReportList>>({
		api: '/visit-reports/fh/get-visit-report-list',
		body: defaultParams,
	});
};

export const getCHVisitReportList = (params?: FleetQueryParams) => {
	const defaultParams = params
		? params
		: { pagination: { pageSize: 20, pageNumber: 1 } };
	return fetcher<FleetPaginated<VisitReportList>>({
		api: '/visit-reports/ch/get-visit-report-list',
		body: defaultParams,
	});
};

export const getFBDHVisitReportDetail = (prospectId: string) => {
	return fetcher<{
		mainInformation: MainInformation;
		customerLegalDocuments: CustomerLegalDocument[];
		customerBusinessDocuments: CustomerBusinessDocument[];
		customerReferralDocuments: CustomerLegalDocument[];
		customerAddress: {
			address: string;
			postalDetail: AddressMasterData;
			rt: string;
			rw: string;
		};
		surveyInformation: SurveyInformation;
		siteReport: SiteReport;
		officeObservation: {
			technicalAspect: TechnicalAspect;
			warehouse: OfficeObservationWarehouse;
			collateral: {
				collateralNotes: string;
				collateralVerificationNotes: string;
				collaterals: OfficeObservationCollateral[];
			};
			marketingAspect: MarketingAspect & {
				surveyOfficeObservationId: null;
				salesMethods: OfficeObservationSalesMethod[];
				paymentSystem: OfficeObservationPaymentSystem[];
				competitors: OfficeObservationCompetitor[];
			};
		};
		approval: {
			approvalHistories: ApprovalHistory[];
		};
	}>({
		api: '/visit-reports/fbdh/get-visit-report-detail',
		body: {
			prospectId,
		},
	});
};

export const getCCDHVisitReportDetail = (prospectId: string) => {
	return fetcher<{
		mainInformation: MainInformation;
		customerLegalDocuments: CustomerLegalDocument[];
		customerBusinessDocuments: CustomerBusinessDocument[];
		customerReferralDocuments: CustomerLegalDocument[];
		customerAddress: {
			address: string;
			postalDetail: AddressMasterData;
			rt: string;
			rw: string;
		};
		surveyInformation: SurveyInformation;
		siteReport: SiteReport;
		officeObservation: {
			technicalAspect: TechnicalAspect;
			warehouse: OfficeObservationWarehouse;
			collateral: {
				collateralNotes: string;
				collateralVerificationNotes: string;
				collaterals: OfficeObservationCollateral[];
			};
			marketingAspect: MarketingAspect & {
				surveyOfficeObservationId: null;
				salesMethods: OfficeObservationSalesMethod[];
				paymentSystem: OfficeObservationPaymentSystem[];
				competitors: OfficeObservationCompetitor[];
			};
		};
		approval: {
			approvalHistories: ApprovalHistory[];
		};
	}>({
		api: '/visit-reports/ccdh/get-visit-report-detail',
		body: {
			prospectId,
		},
	});
};

export const getFHVisitReportDetail = (prospectId: string) => {
	return fetcher<{
		mainInformation: MainInformation;
		customerLegalDocuments: CustomerLegalDocument[];
		customerBusinessDocuments: CustomerBusinessDocument[];
		customerReferralDocuments: CustomerLegalDocument[];
		customerAddress: {
			address: string;
			postalDetail: AddressMasterData;
			rt: string;
			rw: string;
		};
		surveyInformation: SurveyInformation;
		siteReport: SiteReport;
		officeObservation: {
			technicalAspect: TechnicalAspect;
			warehouse: OfficeObservationWarehouse;
			collateral: {
				collateralNotes: string;
				collateralVerificationNotes: string;
				collaterals: OfficeObservationCollateral[];
			};
			marketingAspect: MarketingAspect & {
				surveyOfficeObservationId: null;
				salesMethods: OfficeObservationSalesMethod[];
				paymentSystem: OfficeObservationPaymentSystem[];
				competitors: OfficeObservationCompetitor[];
			};
		};
		approval: {
			approvalHistories: ApprovalHistory[];
		};
	}>({
		api: '/visit-reports/fh/get-visit-report-detail',
		body: {
			prospectId,
		},
	});
};

export const getCHVisitReportDetail = (prospectId: string) => {
	return fetcher<{
		mainInformation: MainInformation;
		customerLegalDocuments: CustomerLegalDocument[];
		customerBusinessDocuments: CustomerBusinessDocument[];
		customerReferralDocuments: CustomerLegalDocument[];
		customerAddress: {
			address: string;
			postalDetail: AddressMasterData;
			rt: string;
			rw: string;
		};
		surveyInformation: SurveyInformation;
		siteReport: SiteReport;
		officeObservation: {
			technicalAspect: TechnicalAspect;
			warehouse: OfficeObservationWarehouse;
			collateral: {
				collateralNotes: string;
				collateralVerificationNotes: string;
				collaterals: OfficeObservationCollateral[];
			};
			marketingAspect: MarketingAspect & {
				surveyOfficeObservationId: null;
				salesMethods: OfficeObservationSalesMethod[];
				paymentSystem: OfficeObservationPaymentSystem[];
				competitors: OfficeObservationCompetitor[];
			};
		};
		approval: {
			approvalHistories: ApprovalHistory[];
		};
	}>({
		api: '/visit-reports/ch/get-visit-report-detail',
		body: {
			prospectId,
		},
	});
};

export const submitCCAVisitReportApproval = (body: {
	surveyId: string;
	notes: string;
}) => {
	return fetcher({
		api: '/visit-reports/cca/request-approval',
		body,
	});
};

export const submitFBDHVisitReportApproval = (body: {
	surveyId: number;
	notes: string;
	approvalStatus: string;
}) => {
	return fetcher({
		api: '/visit-reports/fbdh/submit-approval',
		body,
	});
};

export const submitCCDHVisitReportApproval = (body: {
	surveyId: number;
	notes: string;
	approvalStatus: string;
}) => {
	return fetcher({
		api: '/visit-reports/ccdh/submit-approval',
		body,
	});
};

export const submitFHVisitReportApproval = (body: {
	surveyId: number;
	notes: string;
	approvalStatus: string;
}) => {
	return fetcher({
		api: '/visit-reports/fh/submit-approval',
		body,
	});
};

export const submitCHVisitReportApproval = (body: {
	surveyId: number;
	notes: string;
	approvalStatus: string;
}) => {
	return fetcher({
		api: '/visit-reports/ch/submit-approval',
		body,
	});
};

export type VisitReportOfficePdfDetail = {
	mainInformation: {
		prospectId: string;
		orderSource: string;
		customerId: string;
		customerName: string;
		prospectStatus: string;
		prospectReviewStatus: string;
		customerGroup: {
			id: string;
			value: string;
		};
		industrySector: {
			id: number;
			sectorLevel5: string;
			sectorLevel4: string;
			sectorLevel3: string;
			sectorLevel2: string;
			sectorLevel1: string;
			industryClass: string;
			ecosystem: string;
			code: string | null;
			portofolioGuidelineId: number;
			portofolioGuideline: string;
		};
		slikResult: number;
		frm: MasterData;
		fbdh: MasterData;
		creditAnalyst: {
			id: number;
			value: string;
		};
	};
	customerLegalDocuments: Array<{
		id: number;
		documentType: string;
		documentNo: string;
		documentDate: string | null;
		isMandatory: boolean | null;
		isMandatory2: boolean | null;
		isNoteMandatory: boolean | null;
		notes: string | null;
		order: number | null;
		file: {
			id: string;
			name: string;
			path: string;
			url: string;
			format: string;
		};
		defaultDocumentNo: string | null;
		isDocumentNoMandatory: boolean | null;
		isDocumentDateMandatory: boolean | null;
	}>;
	customerBusinessDocuments: Array<{
		id: number;
		documentType: string;
		documentNo: string;
		documentDate: string | null;
		publisher: string | null;
		validityPeriod: string | null;
		isMandatory: boolean | null;
		file: {
			id: string;
			name: string;
			path: string;
			url: string;
			format: string;
		};
		defaultDocumentNo: string | null;
		isDocumentNoMandatory: boolean | null;
		isDocumentDateMandatory: boolean | null;
		publishers: string | null;
		validityPeriods: string | null;
		isMandatory2: boolean | null;
		isNoteMandatory: boolean | null;
		notes: string | null;
		order: number | null;
	}>;
	customerReferralDocuments: Array<CustomerReferralDocument>;
	customerAddress: {
		address: string;
		rt: string;
		rw: string;
		postalDetail: {
			postalId: number;
			postalCode: string;
			locationName: string;
			districtName: string;
			cityName: string;
			provinceName: string;
		};
	};
	surveyInformation: {
		id: number;
		department: string | null;
		officeAddress: string;
		officeLat: number;
		officeLong: number;
		surveyOfficeDate: string;
		siteAddress: string;
		siteLat: number;
		siteLong: number;
		surveySiteDate: string;
		surveyParticipants: Array<{
			id: number;
			userId: number | null;
			name: string;
			position: string;
			isRequired: boolean;
		}>;
	};
	officeObservation: {
		technicalAspect: {
			officeAddress: string;
			officeRt: string;
			officeRw: string;
			isOfficeAddressSameAsCustomer: boolean;
			officePostalCode: {
				postalId: number;
				postalCode: string;
				locationName: string;
				districtName: string;
				cityName: string;
				provinceName: string;
			};
			startedBusinessYear: string;
			previousBusiness: string;
			companyEstablishmentDate: string;
			officePhoneNo: string;
			branchLocation: string;
			numberCurrentBranch: number;
			officeOwnershipStatus: {
				id: number;
				value: string;
			};
			officeOccupationDate: string;
			officeRentalStartDate: string | null;
			officeRentalEndDate: string | null;
			officeAnnualRentalFee: number | null;
			officeHeadcount: number;
			technicalAspectNotes: string;
			technicalAspectVerificationNotes: string;
			unitDocumentFile: {
				id: string;
				name: string;
				path: string;
				url: string;
				format: string;
			};
			attendees: Array<{
				id: number;
				name: string;
				position: string;
				companyName: string;
			}>;
			units: Array<{
				id: number;
				unitAmount: number;
				unitBrandDetail: string;
			}>;
			photos: Array<{
				id: number;
				name: string;
				description: string;
				photo: {
					id: string;
					name: string;
					path: string;
					url: string;
					format: string;
				};
			}>;
		};
		marketingAspect: {
			surveyId: number;
			surveyOfficeObservationId: number;
			productOffer: string;
			isOtherBusiness: boolean;
			otherBusiness: string | null;
			otherBusinessPortion: string | null;
			marketingAspectNotes: string;
			marketingAspectVerificationNotes: string;
			salesMethods: Array<{
				id: number;
				surveyId: number;
				salesMethod: {
					id: number;
					value: string;
				};
				methodDetail: string;
			}>;
			paymentSystem: Array<{
				id: number;
				surveyId: number;
				paymentSystem: {
					id: number;
					value: string;
				};
				portionPercentage: string;
			}>;
			competitors: Array<{
				id: number;
				surveyId: number;
				name: string;
			}>;
			bouwheers: Array<{
				id: number;
				prospectId: string;
				bouwheer: {
					id: string;
					name: string;
					type: string;
					industrySector: {
						id: number;
						sectorLevel5: string;
						sectorLevel4: string;
						sectorLevel3: string;
						sectorLevel2: string;
						sectorLevel1: string;
						industryClass: string;
						ecosystem: string;
						code: string | null;
						portofolioGuidelineId: number | null;
						portofolioGuideline: string;
					};
				};
				jobTitle: string;
				spkNumber: string;
				jobLocation: string;
				contractDetail: string;
				contractValue: number;
				termOfPayment: string;
				contractDocument: {
					id: string;
					name: string;
					path: string;
					url: string;
					format: string;
				};
				startDate: string;
				endDate: string;
				industrySector: {
					id: number;
					sectorLevel5: string;
					sectorLevel4: string;
					sectorLevel3: string;
					sectorLevel2: string;
					sectorLevel1: string;
					industryClass: string;
					ecosystem: string;
					code: string | null;
					portofolioGuidelineId: number | null;
					portofolioGuideline: string;
				};
			}>;
		};
		warehouse: {
			surveyId: number;
			surveyOfficeObservationId: number;
			warehouseAddress: string;
			warehouseRt: string;
			warehouseRw: string;
			isWarehouseAddressSameAsCustomer: boolean;
			warehousePostalCode: {
				postalId: number;
				postalCode: string;
				locationName: string;
				districtName: string;
				cityName: string;
				provinceName: string;
			};
			warehousePhoneNo: string;
			warehouseOwnershipStatus: {
				id: number;
				value: string;
			};
			warehouseOccupationDate: string;
			warehouseRentalStartDate: string | null;
			warehouseRentalEndDate: string | null;
			warehouseAnnualRentalFee: number | null;
			warehouseSize: number;
			warehouseCapacity: number;
			warehouseHeadcount: number;
			specializedExpertise: string;
			materialOwnershipType: {
				id: number;
				value: string;
			};
			materialOwnershipPortion: string | null;
			totalReceivables: number;
			warehouseNotes: string;
			warehouseVerificationNotes: string;
			spareParts: Array<{
				id: number;
				surveyId: number;
				name: string;
				quantity: number;
			}>;
			inventories: Array<{
				id: number;
				surveyId: number;
				name: string;
				quantity: number;
				type: {
					id: number;
					value: string;
				};
			}>;
			paretoSuppliers: Array<{
				id: number;
				surveyId: number;
				supplierName: string;
				phoneNumber: string;
				monthlyPurchase: number;
				paymentSystem: string;
				constraint: string | null;
			}>;
			paretoReceivables: Array<{
				id: number;
				surveyId: number;
				bouwheerName: string;
				phoneNumber: string;
				jobName: string;
				receivableValue: number;
				oldestReceivableAge: number;
				constraint: string | null;
			}>;
		};
		collateral: {
			collaterals: Array<{
				id: number;
				prospectSurveyId: number | null;
				collateralType: string;
				brandTypeAndCapacity: string;
				yearOfAssembly: string;
				ownership: string;
				hoursMeters: string;
				physicalCondition: string;
				functionCondition: string;
				additionalInformation: string | null;
				collateralVerificationNotes: string | null;
			}>;
			collateralNotes: string;
			collateralVerificationNotes: string;
		};
	};
	technicalAspect: {
		officeAddress: string;
		officeRt: string;
		officeRw: string;
		isOfficeAddressSameAsCustomer: boolean;
		officePostalCode: {
			postalId: number;
			postalCode: string;
			locationName: string;
			districtName: string;
			cityName: string;
			provinceName: string;
		};
		startedBusinessYear: string;
		previousBusiness: string;
		companyEstablishmentDate: string;
		officePhoneNo: string;
		branchLocation: string;
		numberCurrentBranch: number;
		officeOwnershipStatus: {
			id: number;
			value: string;
		};
		officeOccupationDate: string;
		officeRentalStartDate: string | null;
		officeRentalEndDate: string | null;
		officeAnnualRentalFee: number | null;
		officeHeadcount: number;
		technicalAspectNotes: string;
		technicalAspectVerificationNotes: string;
		unitDocumentFile: {
			id: string;
			name: string;
			path: string;
			url: string;
			format: string;
		};
		attendees: Array<{
			id: number;
			name: string;
			position: string;
			companyName: string;
		}>;
		units: Array<{
			id: number;
			unitAmount: number;
			unitBrandDetail: string;
		}>;
		photos: Array<{
			id: number;
			name: string;
			description: string;
			photo: {
				id: string;
				name: string;
				path: string;
				url: string;
				format: string;
			};
		}>;
	};
	marketingAspect: {
		surveyId: number;
		surveyOfficeObservationId: number;
		productOffer: string;
		isOtherBusiness: boolean;
		otherBusiness: string | null;
		otherBusinessPortion: string | null;
		marketingAspectNotes: string;
		marketingAspectVerificationNotes: string;
		salesMethods: Array<{
			id: number;
			surveyId: number;
			salesMethod: {
				id: number;
				value: string;
			};
			methodDetail: string;
		}>;
		paymentSystem: Array<{
			id: number;
			surveyId: number;
			paymentSystem: {
				id: number;
				value: string;
			};
			portionPercentage: string;
		}>;
		competitors: Array<{
			id: number;
			surveyId: number;
			name: string;
		}>;
		bouwheers: Array<{
			id: number;
			prospectId: string;
			bouwheer: {
				id: string;
				name: string;
				type: string;
				industrySector: {
					id: number;
					sectorLevel5: string;
					sectorLevel4: string;
					sectorLevel3: string;
					sectorLevel2: string;
					sectorLevel1: string;
					industryClass: string;
					ecosystem: string;
					code: string | null;
					portofolioGuidelineId: number | null;
					portofolioGuideline: string;
				};
			};
			jobTitle: string;
			spkNumber: string;
			jobLocation: string;
			contractDetail: string;
			contractValue: number;
			termOfPayment: string;
			contractDocument: {
				id: string;
				name: string;
				path: string;
				url: string;
				format: string;
			};
			startDate: string;
			endDate: string;
			industrySector: {
				id: number;
				sectorLevel5: string;
				sectorLevel4: string;
				sectorLevel3: string;
				sectorLevel2: string;
				sectorLevel1: string;
				industryClass: string;
				ecosystem: string;
				code: string | null;
				portofolioGuidelineId: number | null;
				portofolioGuideline: string;
			};
		}>;
	};
	warehouse: {
		surveyId: number;
		surveyOfficeObservationId: number;
		warehouseAddress: string;
		warehouseRt: string;
		warehouseRw: string;
		isWarehouseAddressSameAsCustomer: boolean;
		warehousePostalCode: {
			postalId: number;
			postalCode: string;
			locationName: string;
			districtName: string;
			cityName: string;
			provinceName: string;
		};
		warehousePhoneNo: string;
		warehouseOwnershipStatus: {
			id: number;
			value: string;
		};
		warehouseOccupationDate: string;
		warehouseRentalStartDate: string | null;
		warehouseRentalEndDate: string | null;
		warehouseAnnualRentalFee: number | null;
		warehouseSize: number;
		warehouseCapacity: number;
		warehouseHeadcount: number;
		specializedExpertise: string;
		materialOwnershipType: {
			id: number;
			value: string;
		};
		materialOwnershipPortion: string | null;
		totalReceivables: number;
		warehouseNotes: string;
		warehouseVerificationNotes: string;
		spareParts: Array<{
			id: number;
			surveyId: number;
			name: string;
			quantity: number;
		}>;
		inventories: Array<{
			id: number;
			surveyId: number;
			name: string;
			quantity: number;
			type: {
				id: number;
				value: string;
			};
		}>;
		paretoSuppliers: Array<{
			id: number;
			surveyId: number;
			supplierName: string;
			phoneNumber: string;
			monthlyPurchase: number;
			paymentSystem: string;
			constraint: string | null;
		}>;
		paretoReceivables: Array<{
			id: number;
			surveyId: number;
			bouwheerName: string;
			phoneNumber: string;
			jobName: string;
			receivableValue: number;
			oldestReceivableAge: number;
			constraint: string | null;
		}>;
	};
	collaterals: Array<{
		id: number;
		prospectSurveyId: number | null;
		collateralType: string;
		brandTypeAndCapacity: string;
		yearOfAssembly: string;
		ownership: string;
		hoursMeters: string;
		physicalCondition: string;
		functionCondition: string;
		additionalInformation: string | null;
		collateralVerificationNotes: string | null;
	}>;
	collateralNotes: string;
	approvalHistories: Array<{
		no: number;
		approvalProcess: string;
		approvalDate: string;
		approvalBy: string;
		approvalNote: string;
		approvalStatus: string;
		rejectCategory: string | null;
	}>;
};

export const getVisitReportOfficePdfDetail = (prospectId: string) => {
	return fetcher<VisitReportOfficePdfDetail>({
		api: '/visit-reports/office-observations/get-pdf-detail',
		body: {
			prospectId,
		},
		handleError: false,
	});
};

// WIP interface site report pdf detail
export type VisitReportSitePdfDetail = {
	mainInformation: MainInformation;
	surveyInformation?: SurveyInformation;
	siteReport: SiteReport;
	approvalHistories: Array<{
		no: number;
		approvalProcess: string;
		approvalDate: string;
		approvalBy: string;
	}>;
};

export const getVisitReportSitePdfDetail = (prospectId: string) => {
	return fetcher<VisitReportSitePdfDetail>({
		api: '/visit-reports/site-report/get-pdf-detail',
		body: {
			prospectId,
		},
		handleError: false,
	});
};

export const getFRMCallReportList = (body: FleetGetListParams) => {
	return fetcher<
		FleetPaginated<{
			no: null | string;
			prospectId: string;
			callReportId: null | string;
			customerId: string;
			customerName: string;
			prospectStatus: MasterData;
			customerGroup: MasterData;
			industrySector: IndustryMasterData;
			newOrderDate: string;
			frmName: string;
			businessHeadName: string;
			npwp: string;
		}>
	>({
		api: '/visit-reports/call-reports/frm/get-list',
		body,
	});
};

export const getFRMCallReportHistories = (prospectId: string) => {
	return fetcher<{
		surveyCallReports: {
			customerName?: string;
			surveyCallReportId: number;
			prospectId: string;
			confirmationMedia: MasterData;
			confirmationDate: string;
			evidenceFile: FleetFile;
			contacts: [
				{
					id: number;
					prospectId: null;
					name: string;
					position: string;
					companyName: string;
					phoneNumber: string;
				},
			];
			confirmations: {
				id: 1;
				confirmationPoint: string;
				confirmationDetail: string;
			}[];
		}[];
	}>({
		api: '/visit-reports/call-reports/frm/get-history',
		body: {
			prospectId,
		},
	});
};

export const getFRMCallReportDetail = (prospectId: string) => {
	return fetcher<{
		mainInformation: MainInformation;
		customerLegalDocuments: CustomerLegalDocument[];
		customerBusinessDocuments: CustomerBusinessDocument[];
		customerReferralDocuments: CustomerLegalDocument[];
		surveyCallContacts: unknown[];
		surveyCallReportDetail: unknown;
		surveyInformation: SurveyInformation;
		surveySiteReportPhotos: unknown[];
	}>({
		api: '/visit-reports/call-reports/frm/get-detail',
		body: {
			prospectId,
		},
	});
};

export const getFRMCallReportDetailWithId = (
	id: string,
	prospectId: string,
) => {
	return fetcher<{
		mainInformation: MainInformation;
		customerLegalDocuments: CustomerLegalDocument[];
		customerBusinessDocuments: CustomerBusinessDocument[];
		customerReferralDocuments: CustomerLegalDocument[];
		surveyCallContacts: unknown[];
		surveyCallReportDetail: unknown;
		surveyInformation: SurveyInformation;
		surveySiteReportPhotos: unknown[];
	}>({
		api: '/visit-reports/call-reports/frm/get-detail',
		body: {
			id,
			prospectId,
		},
	});
};

export type SubmitFRMCallReportPayload = {
	id?: number;
	prospectId: string;
	confirmationDate: string;
	confirmationMedia: number;
	additionalInformation?: string;
	supportingDocument: {
		id: string;
		name: string;
		path: string;
	};
	surveyCallContacts: {
		id: number;
		surveyId: number;
		name: string;
		position: string;
		companyName: string;
		phoneNumber: string;
	}[];
	surveyCallConfirmations: {
		id: number;
		confirmationPoint: string;
		confirmationDetail: string;
	}[];
};

export const submitFRMCallReport = (body: SubmitFRMCallReportPayload) => {
	return fetcher({
		api: '/visit-reports/call-reports/frm/submit-call-report',
		body,
	});
};
